<template>
    <app-template>
        <!-- MODALE -->
        <modal name="attestation-create" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-6 space-y-6 overflow-y-auto max-h-[100vh]">

                <!-- 🔍 RECHERCHE CLIENT -->
                <section>
                    <h3 class="font-semibold mb-2">Client</h3>

                    <div v-if="!selectedClient">
                        <input
                            v-model="searchClient"
                            placeholder="Nom, téléphone, email…"
                            class="w-full border rounded px-3 py-2"
                        />

                        <div v-if="loadingClients" class="text-center py-4">
                            <i class="fas fa-spinner fa-spin"></i>
                        </div>

                        <div
                            v-for="c in clients"
                            :key="c.id"
                            @click="selectClient(c)"
                            class="mt-2 p-2 border rounded cursor-pointer hover:bg-gray-100"
                        >
                            <strong>{{ c.nom }}</strong>
                            <div class="text-xs text-gray-500">{{ c.email }}</div>
                        </div>
                    </div>

                    <div v-if="selectedClient" class="mt-3 p-3 bg-gray-50 rounded flex justify-between items-center">
                        <div>
                            <strong>{{ selectedClient.nom }}</strong>
                            <div class="text-xs text-gray-500">{{ selectedClient.email }}</div>
                        </div>
                        <button @click="resetClient" class="text-sm text-blue-600 underline">
                            Modifier
                        </button>
                    </div>
                </section>

                <!-- ⛔ PAS DE CLIENT -->
                <div v-if="!selectedClient" class="p-4 bg-yellow-50 border-l-4 border-yellow-500 text-sm">
                    Sélectionnez un client pour commencer l’attestation.
                </div>

                <!-- ✅ FORMULAIRE -->
                <template v-if="selectedClient">
                    <div class="grid grid-cols-2 gap-4">
                        <!-- APPAREIL -->
                        <section>
                            <h3 class="font-semibold mb-2">Appareil</h3>
                            <select v-model="form.appareil_id" class="w-full border rounded px-3 py-2">
                                <option disabled value="">Sélectionner un appareil</option>
                                <option v-for="a in selectedClient.appareils" :key="a.id" :value="a.id">
                                    {{ a.marque }} – {{ a.model }}
                                </option>
                            </select>
                        </section>

                        <!-- ADRESSE -->
                        <section v-if="selectedAppareil">
                            <h3 class="font-semibold mb-2">Adresse d’intervention</h3>
                            <select v-model="form.adresse_id" class="w-full border rounded px-3 py-2">
                                <option disabled value="">Sélectionner une adresse</option>
                                <option v-for="a in selectedClient.adresses" :key="a.id" :value="a.id">
                                    {{ a.adresse1 }} – {{ a.ville }}
                                </option>
                            </select>
                        </section>

                        <!-- DATE D'INTERVENTION -->
                        <section>
                            <h3 class="font-semibold mb-2">Date d’intervention</h3>
                            <input
                                type="date"
                                v-model="form.date_intervention"
                                class="w-full border rounded px-3 py-2"
                            />
                        </section>
                    </div>

                    <!-- STANDARD -->
                    <section class="p-4 bg-blue-50 border-l-4 border-blue-500">
                        <label class="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                v-model="standard"
                                @change="toggleStandard"
                            />
                            <span class="font-semibold">
                                Entretien standard réalisé
                            </span>
                        </label>
                    </section>

                    <!-- CONTROLES -->
                    <section>
                        <h3 class="font-semibold mb-2">Contrôles</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <label
                                v-for="(label, key) in controls"
                                :key="key"
                                class="flex items-center gap-2 text-sm"
                            >
                                <input type="checkbox" v-model="form[key]" />
                                {{ label }}
                            </label>
                        </div>
                    </section>

                    <!-- CAS PARTICULIERS -->
                    <section>
                        <h3 class="font-semibold mb-2">Cas particuliers</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <label
                                v-for="(label, key) in nonStandardFields"
                                :key="key"
                                class="flex items-center gap-2 text-sm"
                            >
                                <input type="checkbox" v-model="form[key]" />
                                {{ label }}
                            </label>
                        </div>
                    </section>

                    <!-- MESURES -->
                    <section>
                        <h3 class="font-semibold mb-2">Mesures (si réalisées)</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div v-for="(cfg, key) in measureFields" :key="key">
                                <label class="text-xs text-gray-600">
                                    {{ cfg.label }}
                                </label>
                                <input
                                    v-model.number="form[key]"
                                    :type="cfg.type"
                                    :step="cfg.step"
                                    class="w-full border rounded px-2 py-1 text-sm"
                                />
                            </div>
                        </div>
                    </section>

                    <!-- COMMENTAIRE -->
                    <section>
                        <h3 class="font-semibold mb-2">Commentaire du technicien</h3>

                        <div class="flex flex-wrap gap-2 mb-2">
                            <button
                                v-for="preset in commentPresets"
                                :key="preset"
                                @click="appendComment(preset)"
                                class="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                            >
                                {{ preset }}
                            </button>
                        </div>

                        <textarea
                            v-model="form.commentaire_intervenant"
                            class="w-full border rounded px-3 py-2"
                            rows="3"
                            placeholder="RAS, réglage effectué…"
                        />
                    </section>

                    <!-- ACTION -->
                    <section class="flex justify-end gap-3 pt-4 border-t">
                        <button @click="$modal.hide('attestation-create')" class="border px-4 py-2 rounded">
                            Annuler
                        </button>
                        <button
                            @click="submit"
                            :disabled="loading || !form.adresse_id"
                            class="bg-blue-600 text-white px-4 py-2 rounded"
                        >
                            <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
                            Enregistrer
                        </button>
                    </section>
                </template>
            </div>
        </modal>

        <!-- HEADER -->
        <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold flex items-center gap-2">
                <i class="fas fa-file-signature text-blue-600"></i>
                Attestations d’entretien
            </h2>

            <button
                @click="open"
                class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
                <i class="fas fa-plus mr-2"></i>
                Nouvelle attestation
            </button>
        </div>

        <!-- FILTRES -->
        <div class="bg-white p-4 rounded shadow mb-6">
            <div class="grid grid-cols-1 md:grid-cols-6 gap-4">

                <div>
                    <label class="text-sm text-gray-600">N° attestation</label>
                    <input
                        v-model="filters.numero"
                        @keyup.enter="fetch"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                        placeholder="AE-0126-0098"
                    />
                </div>

                <div>
                    <label class="text-sm text-gray-600">Client</label>
                    <input
                        v-model="filters.client"
                        @keyup.enter="fetch"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                        placeholder="Nom client"
                    />
                </div>

                <div>
                    <label class="text-sm text-gray-600">Intervenant</label>
                    <select
                        v-model="filters.intervenant_id"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                    >
                        <option value="">-</option>
                        <option
                            v-for="technicien in sortedTechniciens"
                            :key="technicien.id"
                            :value="technicien.id"
                        >
                            {{ technicien.prenom }} {{ technicien.nom }}
                        </option>
                    </select>
                </div>

                <div>
                    <label class="text-sm text-gray-600">Du</label>
                    <input
                        type="date"
                        v-model="filters.date_debut"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                    />
                </div>

                <div>
                    <label class="text-sm text-gray-600">Au</label>
                    <input
                        type="date"
                        v-model="filters.date_fin"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                    />
                </div>

                <div>
                    <label class="text-sm text-gray-600">Signature</label>
                    <select
                        v-model="filters.signed"
                        class="w-full border rounded px-3 py-2 focus:outline-none"
                    >
                        <option value="">-</option>
                        <option value="1">Signées</option>
                        <option value="0">Non signées</option>
                    </select>
                </div>

            </div>
        </div>

        <!-- TABLE -->
        <div v-if="loading" class="text-center py-10">
            <i class="fas fa-spinner fa-spin text-2xl text-gray-500"></i>
        </div>

        <div v-else-if="!attestations || !attestations.length" class="text-center py-10 text-gray-500">
            Aucune attestation trouvée
        </div>

        <div v-else class="bg-white rounded shadow overflow-x-auto">
            <table class="min-w-full text-sm">
                <thead class="bg-gray-100 text-gray-700">
                    <tr>
                        <th class="px-4 py-3 text-left">N°</th>
                        <th class="px-4 py-3 text-left">Client</th>
                        <th class="px-4 py-3 text-left">Adresse</th>
                        <th class="px-4 py-3 text-left">Date</th>
                        <th class="px-4 py-3 text-left">Intervenant</th>
                        <th class="px-4 py-3 text-left">Statut</th>
                        <th class="px-4 py-3 text-left">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="attestation in attestations"
                        :key="attestation.id"
                        class="border-t"
                    >
                        <td class="px-4 py-2 font-mono">
                            {{ attestation.numero_attestation }}
                        </td>

                        <td class="px-4 py-2">
                            {{ attestation.client?.nom || '-' }}
                        </td>

                        <td class="px-4 py-2">
                            {{ attestation.adresse?.ville || '-' }}
                        </td>

                        <td class="px-4 py-2">
                            {{ formatDate(attestation.date_intervention) }}
                        </td>

                        <td class="px-4 py-2">
                            {{ attestation.intervenant?.nom || '-' }}
                        </td>

                        <td class="px-4 py-2">
                            <span
                                v-if="attestation.signed_at"
                                class="text-green-600 font-semibold"
                            >
                                Signée
                            </span>
                            <span v-else class="text-orange-500">
                                En attente
                            </span>
                        </td>

                        <td class="px-4 py-2">
                            <a
                                :href="attestation.url_document"
                                target="_blank"
                                class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                            >
                                <i class="fas fa-eye mr-1"></i>
                                Consulter
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- PAGINATION -->
        <div
            v-if="pagination.last_page > 1"
            class="flex items-center justify-between mt-6"
        >
            <span class="text-sm text-gray-500">
                {{ pagination.total }} attestations
            </span>

            <div class="flex gap-2">
                <button
                    @click="fetch(pagination.page - 1)"
                    :disabled="pagination.page === 1"
                    class="px-3 py-1 border rounded disabled:opacity-40"
                >
                    Précédent
                </button>

                <span class="text-sm">
                    Page {{ pagination.page }} / {{ pagination.last_page }}
                </span>

                <button
                    @click="fetch(pagination.page + 1)"
                    :disabled="pagination.page === pagination.last_page"
                    class="px-3 py-1 border rounded disabled:opacity-40"
                >
                    Suivant
                </button>
            </div>
        </div>
    </app-template>
</template>

<script>
import moment from 'moment';

export default {
    data() {
        return {
            searchClient: '',
            clients: [],
            loadingClients: false,
            selectedClient: null,
            loading: false,

            standard: true,

            form: {},

            standardFields: [
                'nettoyage_bruleur',
                'verification_circulateur',
                'reglage_organes_regulation',
                'verification_dispositifs_securite',
                'verification_conduit_raccordement',
                'controle_vacuite_conduit',
                'ramonage_intermediaire',
                'reglage_regulation_exterieure',
                'mise_en_marche_saison',
            ],

            controls: {
                nettoyage_bruleur: 'Nettoyage brûleur',
                verification_circulateur: 'Circulateur',
                reglage_organes_regulation: 'Régulation',
                verification_dispositifs_securite: 'Sécurité',
                verification_conduit_raccordement: 'Conduit',
                controle_vacuite_conduit: 'Vacuité conduit',
                ramonage_intermediaire: 'Ramonage',
                reglage_regulation_exterieure: 'Régulation extérieure',
                mise_en_marche_saison: 'Mise en service saison',
            },

            nonStandardFields: {
                vmc_gaz_verification_securite: 'Sécurité VMC gaz',
                vmc_gaz_nettoyage_conduit: 'Nettoyage conduit VMC',
                verification_debit_gaz: 'Vérification débit gaz',
                ballon_accumulation_verification_anodes: 'Vérification anodes ballon',
                entretien_radiateurs: 'Entretien radiateurs',
                detartrage: 'Détartrage',
                entretien_vmc: 'Entretien VMC',
            },

            measureFields: {
                temperature_fumees: {
                    label: 'Température des fumées (°C)',
                    type: 'number',
                    step: '0.1',
                },
                teneur_co2: {
                    label: 'CO₂ (%)',
                    type: 'number',
                    step: '0.1',
                },
                teneur_o2: {
                    label: 'O₂ (%)',
                    type: 'number',
                    step: '0.1',
                },
                rendement_evalue: {
                    label: 'Rendement évalué (%)',
                    type: 'number',
                    step: '0.1',
                },
            },

            commentPresets: [
                'RAS',
                'Réglage effectué',
                'Nettoyage complet',
            ],

            attestations: [],
            filters: {
                numero: '',
                client: '',
                intervenant_id: '',
                date_debut: '',
                date_fin: '',
                signed: '',
            },

            pagination: {
                page: 1,
                last_page: 1,
                total: 0,
            },

            techniciens: [],

            timeout: null,
        };
    },

    watch: {
        searchClient() {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(this.fetchClients, 300);
        },

        isStandardComplete(newVal) {
            // évite les boucles inutiles
            if (this.standard !== newVal) {
                this.standard = newVal;
            }
        },

        filters: {
            deep: true,
            handler() {
                this.fetch(1);
            },
        },
    },

    computed: {
        selectedAppareil() {
            return this.selectedClient?.appareils?.find(
                a => a.id === this.form.appareil_id
            );
        },

        isStandardComplete() {
            return this.standardFields.every(
                field => this.form[field] === true
            );
        },
        sortedTechniciens() {
            return this.techniciens.sort((a, b) => {
                const nameA = a.nom.toUpperCase();
                const nameB = b.nom.toUpperCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });
        },
    },

    methods: {
        open() {
            this.reset();
            this.$modal.show('attestation-create');
            this.form.date_intervention = moment().format('YYYY-MM-DD');
        },

        reset() {
            this.searchClient = '';
            this.clients = [];
            this.selectedClient = null;
            this.standard = true;
            this.standardFields.forEach(f => this.form[f] = true);
            this.form.date_intervention = null;
            this.form.commentaire_intervenant = '';

            this.form = {
                // standard
                nettoyage_bruleur: true,
                verification_circulateur: true,
                reglage_organes_regulation: true,
                verification_dispositifs_securite: true,
                verification_conduit_raccordement: true,
                controle_vacuite_conduit: true,
                ramonage_intermediaire: true,
                reglage_regulation_exterieure: true,
                mise_en_marche_saison: true,

                // non standard
                vmc_gaz_verification_securite: false,
                vmc_gaz_nettoyage_conduit: false,
                verification_debit_gaz: false,
                ballon_accumulation_verification_anodes: false,
                entretien_radiateurs: false,
                detartrage: false,
                entretien_vmc: false,

                // date
                date_intervention: null,

                // mesures
                temperature_fumees: null,
                teneur_co2: null,
                teneur_o2: null,
                rendement_evalue: null,

                // commentaire
                commentaire_intervenant: '',
            }
        },

        toggleStandard() {
            this.standardFields.forEach(field => {
                this.form[field] = this.standard;
            });
        },

        async fetchClients() {
            if (!this.searchClient) {
                this.clients = [];
                return;
            }
            this.loadingClients = true;
            const res = await this.$request.get('/clients', {
                params: { search: this.searchClient, with_appareils: 1, with_adresses: 1 },
            });
            this.clients = res.data;
            this.loadingClients = false;
        },

        selectClient(client) {
            this.selectedClient = client;
            this.form.appareil_id = client.appareils?.[0]?.id || '';
            this.form.adresse_id = client.adresses?.[0]?.id || '';
            this.form.client_id = client.id;
        },

        resetClient() {
            this.reset();
        },

        appendComment(text) {
            if (!this.form.commentaire_intervenant) {
                this.form.commentaire_intervenant = text;
            } else if (!this.form.commentaire_intervenant.includes(text)) {
                this.form.commentaire_intervenant += ' – ' + text;
            }
        },

        async submit() {
            // this.loading = true;
            await this.$request.post('/attestations/store', this.form);
            this.loading = false;
            this.$modal.hide('attestation-create');
        },

        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '-';
        },

        fetch(page = 1) {
            clearTimeout(this.fetchTimeout);

            this.fetchTimeout = setTimeout(async () => {
                this.loading = true;
                this.pagination.page = page;

                const res = await this.$request.get('/attestations/index', {
                    params: {
                        ...this.filters,
                        page: this.pagination.page,
                    },
                });

                this.attestations = res.data;
                this.pagination.last_page = res.last_page;
                this.pagination.total = res.total;

                this.loading = false;
            }, 300);
        },
        async fetchTechniciens() {
            let res = await this.$request.get('/techniciens')
            this.techniciens = res
        },
    },

    mounted() {
        this.fetch();
        this.fetchTechniciens();
    },
};
</script>

<style lang="scss" scoped>

</style>